import { useRef, useState, useId, useEffect, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { winesApi, type LookupKind } from '../api/wines';

/**
 * Free-text typeahead backed by a lookup endpoint. The value is always a plain
 * string — pick a suggestion or type a new name (it gets created server-side on
 * save). Used for producer / region / grape fields in the Cellar form.
 */
export function Combobox({
  value,
  onChange,
  kind,
  placeholder,
  id: idProp,
  className = 'field',
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  kind: LookupKind;
  placeholder?: string;
  id?: string;
  className?: string;
  disabled?: boolean;
}) {
  const autoId = useId();
  const inputId = idProp ?? autoId;
  const listId = `${inputId}-listbox`;
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const blurTimer = useRef<ReturnType<typeof setTimeout>>();

  const { data: items = [] } = useQuery({
    queryKey: ['lookup', kind, value],
    queryFn: () => winesApi.lookup(kind, value),
    enabled: open,
    staleTime: 30_000,
  });

  const suggestions = items.filter((s) => s.toLowerCase() !== value.trim().toLowerCase());

  useEffect(() => {
    setHighlight(0);
  }, [suggestions.length, value]);

  function pick(s: string) {
    clearTimeout(blurTimer.current);
    onChange(s);
    setOpen(false);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && suggestions[highlight]) {
      e.preventDefault();
      pick(suggestions[highlight]!);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div className="relative">
      <input
        id={inputId}
        className={className}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        role="combobox"
        aria-expanded={open && suggestions.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        autoComplete="off"
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          blurTimer.current = setTimeout(() => setOpen(false), 150);
        }}
        onKeyDown={onKeyDown}
      />
      {open && suggestions.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-30 mt-1 max-h-52 w-full overflow-auto rounded-md border border-ink-800 bg-ink-950 py-1 shadow-xl"
        >
          {suggestions.map((s, i) => (
            <li key={s} role="option" aria-selected={i === highlight}>
              <button
                type="button"
                className={`block w-full px-3 py-1.5 text-left text-sm ${
                  i === highlight ? 'bg-ink-800 text-gold-200' : 'text-cream-200 hover:bg-ink-800 hover:text-gold-200'
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(s);
                }}
              >
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
